import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accessibility,
  Baby,
  Briefcase,
  Crown,
  PlaneTakeoff,
  Star,
  Stethoscope,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import {
  ROSTER_PRIORITY_TAGS,
  type RosterPriorityTag,
  type RosterRebookingStatus,
} from '@/lib/disruptions/types';
import { priorityLabel, priorityShortLabel, rebookingStatusLabel } from './labels';

// disruption-roster-mvp-parity (epic NEX-369 / U2) — the rich filter chip row that
// REPLACES the priority + rebooking-status dropdowns on the disruption Passengers
// roster, mirroring the MVP "SCL Severe Storm" AffectedPassengersList:
//
//   [ 👶 UMNR ] [ 👑 VIP ] [ ♿ WCHR ] [ 🩺 MEDA ] [ 💼 Business ] [ ⭐ First ]
//   [ 🛫 Onward ]  |  [ Flight assigned ] [ Options ready ] [ Pending ] …
//
// The chips drive the SAME server-side query params the dropdowns did (`priority`
// + `rebookingStatus` on `useDisruptionRoster`) — the roster contract is single-
// valued per param, so each row is a SINGLE-SELECT toggle: clicking a chip sets the
// filter, clicking the active chip again clears it (back to "all"). This keeps the
// cursor-walk semantics identical (a filter change starts a fresh server walk) — no
// client-side post-filtering.
//
// This is a controlled, stateless component: the roster-tab owns the filter state
// and passes it in, so the chip row has no data/loading/error state of its own (the
// empty / loading / error / success states live on the roster table + the action
// sidebar's progress panel). Friendly labels primary (priorityLabel as the a11y
// name, priorityShortLabel as the compact chip text); white-label tone tokens only
// (the same semantic Tailwind palette + dark: variants the rest of the roster uses).

interface PriorityChip {
  key: RosterPriorityTag;
  icon: ComponentType<{ className?: string }>;
  /** Active (selected) tone — the same palette the roster badges use. */
  tone: string;
}

interface StatusChip {
  key: RosterRebookingStatus;
  tone: string;
}

// 7 priority chips — icon + tone, MVP order (UMNR/VIP/WCHR/MEDA/BUSINESS/FIRST/
// ONWARD_AT_RISK). Keyed off the FROZEN ROSTER_PRIORITY_TAGS so a tag rename can't
// silently drop a chip.
const PRIORITY_CHIPS: PriorityChip[] = [
  { key: 'UMNR', icon: Baby, tone: 'border-rose-300 bg-rose-100 text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300' },
  { key: 'VIP', icon: Crown, tone: 'border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300' },
  { key: 'WCHR', icon: Accessibility, tone: 'border-sky-300 bg-sky-100 text-sky-700 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-300' },
  { key: 'MEDA', icon: Stethoscope, tone: 'border-violet-300 bg-violet-100 text-violet-700 dark:border-violet-900 dark:bg-violet-950/40 dark:text-violet-300' },
  { key: 'BUSINESS', icon: Briefcase, tone: 'border-slate-300 bg-slate-200 text-slate-800 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200' },
  { key: 'FIRST', icon: Star, tone: 'border-yellow-300 bg-yellow-100 text-yellow-800 dark:border-yellow-900 dark:bg-yellow-950/40 dark:text-yellow-300' },
  { key: 'ONWARD_AT_RISK', icon: PlaneTakeoff, tone: 'border-orange-300 bg-orange-100 text-orange-700 dark:border-orange-900 dark:bg-orange-950/40 dark:text-orange-300' },
];

// 6 rebooking-status chips (MVP set — NOT_APPLICABLE is a system state the operator
// doesn't filter to, so it stays off the chip row).
const STATUS_CHIPS: StatusChip[] = [
  { key: 'SELECTED', tone: 'border-emerald-300 bg-emerald-100 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300' },
  { key: 'OPTIONS_READY', tone: 'border-sky-300 bg-sky-100 text-sky-700 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-300' },
  { key: 'PENDING', tone: 'border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300' },
  { key: 'DEFERRED', tone: 'border-slate-300 bg-slate-200 text-slate-700 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300' },
  { key: 'NO_OPTIONS', tone: 'border-rose-300 bg-rose-100 text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300' },
  { key: 'OVERRIDDEN', tone: 'border-violet-300 bg-violet-100 text-violet-700 dark:border-violet-900 dark:bg-violet-950/40 dark:text-violet-300' },
];

const INACTIVE_TONE =
  'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground';

const CHIP_BASE =
  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring';

interface RosterFilterChipsProps {
  priority: RosterPriorityTag | null;
  onPriorityChange: (value: RosterPriorityTag | null) => void;
  rebookingStatus: RosterRebookingStatus | null;
  onRebookingStatusChange: (value: RosterRebookingStatus | null) => void;
}

/**
 * The priority + rebooking-status filter chip row. Single-select toggle per row
 * (the roster's server filter is single-valued): click to select, click again to
 * clear. Emits the raw tag/status so the roster-tab drives the SAME `priority` /
 * `rebookingStatus` query params it did with the dropdowns.
 */
export function RosterFilterChips({
  priority,
  onPriorityChange,
  rebookingStatus,
  onRebookingStatusChange,
}: RosterFilterChipsProps) {
  const { t } = useTranslation('roster');
  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      data-testid="roster-filter-chips"
      role="group"
      aria-label={t('filters.groupLabel')}
    >
      {PRIORITY_CHIPS.map(({ key, icon: Icon, tone }) => {
        const active = priority === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onPriorityChange(active ? null : key)}
            aria-pressed={active}
            aria-label={t('filters.byPriority', { label: priorityLabel(key, t) })}
            title={priorityLabel(key, t)}
            className={cn(CHIP_BASE, active ? tone : INACTIVE_TONE)}
            data-testid={`roster-priority-chip-${key}`}
          >
            <Icon className="h-3 w-3" />
            {priorityShortLabel(key, t)}
          </button>
        );
      })}

      <span className="mx-1 select-none text-muted-foreground/40" aria-hidden>
        |
      </span>

      {STATUS_CHIPS.map(({ key, tone }) => {
        const active = rebookingStatus === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onRebookingStatusChange(active ? null : key)}
            aria-pressed={active}
            aria-label={t('filters.byRebookingStatus', { label: rebookingStatusLabel(key, t) })}
            title={rebookingStatusLabel(key, t)}
            className={cn(CHIP_BASE, active ? tone : INACTIVE_TONE)}
            data-testid={`roster-status-chip-${key}`}
          >
            {rebookingStatusLabel(key, t)}
          </button>
        );
      })}
    </div>
  );
}

// Re-exported so the roster-tab (and tests) can assert the chip set without
// duplicating the tag lists — the priority chips are exactly ROSTER_PRIORITY_TAGS.
export const ROSTER_STATUS_CHIP_KEYS = STATUS_CHIPS.map((c) => c.key);
export { ROSTER_PRIORITY_TAGS };
